/**
 * AdminMyLibrary.js — "My Library" account dashboard page.
 *
 * Lists the logged-in user's purchased online resources and webcasts,
 * fetched from the engine API. Allows search, filtering, and PDF/video access.
 * Protected — redirects to home if not logged in.
 *
 * Props:
 *  - Good {string}                        Time-of-day greeting.
 *  - EngineURL {string}                   Base URL for the engine API.
 *  - Program_structure_images {Array}     Slug-to-image-URL mapping.
 *  - useLoggedIn {boolean}                Whether the user is authenticated.
 *  - loggedInUserData {object}            Logged-in user details.
 *  - UpdateCartQty {Function}             Updates the cart badge.
 *  - UpdateCartAmount {Function}          Updates the cart total.
 *  - ShowLoginPopup {Function}            Opens the login modal.
 *  - ShowLoginPopup_ {Function}           Redirects to profile if logged in, else shows login.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, {Fragment, useEffect, useState, useRef, useCallback} from "react";
import axios from "axios";
import {Link, useHistory, useLocation} from "react-router-dom";
import {SearchIcon, LoaderIcon} from "../../icons";
import DisclaimerSvg from "../../images/disclaimer.svg";
import {Container, Row, Col, Form, FormControl, Modal} from "react-bootstrap";
import {Helmet} from "react-helmet";
import {AdminSidebar} from "./Sidebar/AdminSidebar";

export const AdminMyLibrary = (props) => {

  let history = useHistory();
  const My_Account_button_click = () => {
    if (props.loggedInUserData) history.push("/admin-my-library");
    else props.ShowLoginPopup_();
  };

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const routeLabels = {
    'admin-profile': 'My Profile',
    'admin-my-events': 'Upcoming Events',
    'admin-my-library': 'My Digital Library',
    'admin-cpd-points': 'CPD Points',
    'my-wishlist': 'Bookmarks',
    'admin-orders': 'Billing History',
  };
  const activeLabel = routeLabels[location.pathname.replace('/', '')] || 'Menu';

  // Orders states
  const [myPrograms, setMyPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const searchRef = useRef("");
  const allProgramsRef = useRef([]);
  const program_structure_default_image = "https://s3.ap-southeast-2.amazonaws.com/leo-cussen-store-product-images/Cards/CPD%20Seminar.jpg";

  const [showPopup, setShowPopup] = useState(false);
  const [video_link, setVideoLink] = useState("");
  // Handle Popup
  const handlePopup = (video_link = "") => {
    setShowPopup(!showPopup);
    setVideoLink(video_link);
  };

  const [userEmail, set_user_email] = useState("");
  const [userToken, set_user_token] = useState(undefined);
  const [userLastname, set_user_lastname] = useState("");  

  const getData = useCallback(async function getData() {
    if (userEmail !== "") {
      const api_URL2 = props.EngineURL + "salesforce-query/checkout/leo_library.php";
      const formData = new URLSearchParams();
      formData.append("contact_id", userEmail);
      formData.append("search", search);
      formData.append("lname", userLastname);
      await axios
        .post(api_URL2, formData, {
          crossdomain: true,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${userToken}`,
          },
        })
        .then(function (response) {
          // handle success
          if (response.data.error !== undefined && response.data.error !== "") {
            setLoading(false);
          } else {
            const programs = response.data.program_registrations || [];
            allProgramsRef.current = programs;
            setMyPrograms(programs);
            setLoading(false);
          }
          return response;
        })
        .catch(function (error) {
          // handle error
          setLoading(false);
        })
        .then(function () {
          // always executed
        });
    }
  },[props.EngineURL, userEmail, userLastname, userToken, search]);

  const filterPrograms = (term) => {
    if (!term) {
      setMyPrograms(allProgramsRef.current);
    } else {
      const lower = term.toLowerCase();
      setMyPrograms(
        allProgramsRef.current.filter(
          (p) => p.program_name && p.program_name.toLowerCase().includes(lower)
        )
      );
    }
  };

  useEffect(() => {
    if (props.loggedInUserData !== undefined && props.loggedInUserData !== null) {
      set_user_email(props.loggedInUserData.email);
      set_user_token(props.loggedInUserData.accessToken);
      set_user_lastname(props.loggedInUserData.lastName);
    }
  }, [props.loggedInUserData]);

  useEffect(() => {
    if (userToken) {
      getData();
    }
  }, [userToken, getData]);

  const doLogout = () => {
    axios
      .post(
        props.EngineURL + "create-account/2fa/delete_2fa.php",
        {
          email: userEmail,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`, // Replace `yourToken` with your actual token variable
          },
        }
      )
      .then(function (response) {
        localStorage.removeItem("userDetail");
        localStorage.setItem("isLoggedin", false);
        localStorage.removeItem("checkout_token");
        localStorage.removeItem("thank_you_image");
        localStorage.removeItem("checkout_id");
        localStorage.removeItem("cart_session");
        localStorage.removeItem("create_account_detail");
        localStorage.removeItem("cart_session_sku");
        localStorage.removeItem("isLoggedin");
        localStorage.removeItem("was_book");
        localStorage.removeItem("wishlist_item");
        window.location.href = "/";
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  return (
    <>
    <div className="admin_page admin_my_library">
      <Helmet>
        <title>My Webcast Library | Leo Cussen Centre for Law</title>
      </Helmet>
      {props.loggedInUserData !== undefined && props.useLoggedIn === true ? (
        <Container>
          {/* {user_email} */}
          <div className="admin_wrapper position-relative">
            {props.loggedInUserData !== null && props.loggedInUserData !== undefined ? (
              <div className="admin_details_block">
                <p className="ae-greeting-text">{props.Good},</p>
                <h1 className="ae-greeting-name">{props.loggedInUserData.firstName}</h1>
              </div>
            ) : null}
            <Row>
              <Col xl={3} lg={4} xs={12} className="d-none d-lg-block">
                <AdminSidebar doLogout={doLogout} />
              </Col>
              <Col xl={9} lg={8} xs={12}>
                <div className="admin_right_content_block">
                  <div className="ae-disclaimer">
                    <div className="ae-disclaimer-icon"><img src={DisclaimerSvg} alt="" /></div>
                    <div className="ae-disclaimer-body">
                      <p className="ae-disclaimer-title">Disclaimer</p>
                      <p className="ae-disclaimer-text">CPD customers please note that the digital products in your library are retained for the current and previous CPD years (1 April – 31 March). Download them to ensure access beyond this time. For enquiries contact <a href="mailto:registration@leocussen.edu.au">registration@leocussen.edu.au</a>.</p>
                    </div>
                  </div>
                  <div className="admin_head d-flex flex-wrap align-items-center justify-content-between">
                    <h4 className="m-0">My Webcast Library</h4>
                    <Form
                      className="ae-search-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        filterPrograms(searchRef.current.value);
                      }}
                    >
                      <div
                        className={`ae-search-group${
                          search !== "" && myPrograms.length > 0 ? " ae-search-group--active" : ""
                        }${
                          search !== "" && myPrograms.length === 0 && loading === false ? " ae-search-group--error" : ""
                        }`}
                      >
                        <span className="ae-search-icon"><SearchIcon /></span>
                        <FormControl
                          type="search"
                          placeholder="Start typing"
                          className="ae-search-input"
                          aria-label="Search"
                          ref={searchRef}
                          onChange={(e) => {
                            setSearch(e.target.value);
                            filterPrograms(e.target.value);
                          }}
                        />
                      </div>
                      {search !== "" && myPrograms.length === 0 && loading === false && (
                        <span className="search-no-results-msg">No results found for "{search}"</span>
                      )}
                    </Form>
                  </div>

                  {loading === false ? (
                    myPrograms !== undefined && myPrograms.length > 0 ? (
                      myPrograms.map((program_item) => {
                        let product_name = program_item.program_name;
                        let product_name_slug =
                          program_item.program_slug !== undefined && program_item.program_slug !== null ? program_item.program_slug : "";
                        let program_attendees = program_item.attendees;
                        let program_downloads = program_item.program_downloads;
                        let program_structure = program_item.program_structure.toLowerCase().split(" ").join("-");
                        let program_structure_image = program_structure_default_image;

                        (props.Program_structure_images || []).forEach(function (item) {
                          var keyname = Object.keys(item);
                          if (keyname.includes(program_structure)) {
                            if (item[program_structure] !== undefined && item[program_structure] !== null && item[program_structure].length > 0) {
                              program_structure_image = item[program_structure];
                            }
                          }
                        });

                        return (
                          
                          <div className="lib-card" key={product_name}>
                           
                            <img className="lib-card-img" src={program_structure_image} alt={program_structure} />
                            <div className="lib-card-body">
                              <div className="lib-card-header">
                                <Link to={"/product/" + product_name_slug} className="lib-card-title-link">
                                  <h3 className="lib-card-title">{product_name}</h3>
                                </Link>
                                {program_attendees.length > 0 ? (
                                  <div className="lib-card-attendees">
                                    <span className="lib-attendees-label">Attendees</span>
                                    <span className="lib-attendees-name">{program_attendees.join(", ")}</span>
                                  </div>
                                ) : null}
                              </div>
                              {program_downloads !== undefined && program_downloads.length > 0 ? (
                                <div className="lib-card-downloads">
                                  {program_downloads.map((program_downloads_item) => {
                                    const media_name = program_downloads_item.Name;
                                    const media_download_link = program_downloads_item.NEILON__File_Presigned_URL__c;
                                    const media_type = program_downloads_item.NEILON__Content_Type__c.split("/").reverse()[0];
                                    const isVideo = media_type === "mp4" || media_type === "m4v" || media_type === "mp4v";
                                    return (
                                      <div className="lib-file-block" key={media_name}>
                                        <p className="lib-file-name">{media_name}</p>
                                        <div className="lib-file-actions">
                                          <a href={media_download_link} rel="noreferrer" target="_blank" className="lib-file-action">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M8 1.5v8M4.5 6.5L8 10l3.5-3.5M2 13.5h12" stroke="#6c6c6c" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span>Download {media_type.toUpperCase()}</span>
                                          </a>
                                          {isVideo && (
                                            <button type="button" className="lib-file-action" onClick={() => handlePopup(media_download_link)}>
                                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M4 2.5L13 8l-9 5.5V2.5z" stroke="#6c6c6c" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                              </svg>
                                              <span>Watch Video</span>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="ae-no-events">
                        
                        <p>No records found</p>
                      </div>
                    )
                  ) : (
                     <div className="pre_loader" style={{ position: 'relative'}}>
                      <LoaderIcon />
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      ) : (
        <div className="text-center">
          <h3>Please Login</h3>
          <button onClick={() => My_Account_button_click()} className="btn btn-primary rounded-pill m-0 stest">
            {" "}Login
          </button>
        </div>
      )}
      <>
        <Modal show={showPopup} size="lg" onHide={handlePopup} className="need_help_modal">
          <Modal.Header closeButton className="border-0"></Modal.Header>
          <Modal.Body>
            <video width="100%" height="100%" controls>
              <source src={video_link} type="video/mp4" />
              <source src={video_link} type="video/ogg" />
            </video>
          </Modal.Body>
        </Modal>
      </>
    </div>
    <div className={`admin_sidebar_mobile${mobileSidebarOpen ? ' sidebar-open' : ''}`}>
      <div className="admin_sidebar_drawer">
        <AdminSidebar doLogout={doLogout} />
      </div>
      <button
        className="admin_sidebar_trigger"
        onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        aria-expanded={mobileSidebarOpen}
      >
        <span className="admin_sidebar_trigger_label">{activeLabel}</span>
        <svg className="admin_sidebar_trigger_dots" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="5" r="1.5" fill="#1C1C1C"/>
          <circle cx="12" cy="12" r="1.5" fill="#1C1C1C"/>
          <circle cx="12" cy="19" r="1.5" fill="#1C1C1C"/>
        </svg>
      </button>
    </div>
    </>
  );
};
